import React, { useState } from 'react';
import { combineClasses } from '../../utils/formatters';
import { FORM_CONFIG, TEXT_CONSTANTS } from '../../config';

const USER_TYPE_ICON = {
  [FORM_CONFIG.USER_TYPES.USER]: '👤',
  [FORM_CONFIG.USER_TYPES.PANDIT]: '🕉️',
  [FORM_CONFIG.USER_TYPES.EVENT_PARTNER]: '🎉',
  [FORM_CONFIG.USER_TYPES.SERVICE_PROVIDER]: '🛠️'
};

const SignupForm = ({ onSignup, onSwitchToLogin, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: FORM_CONFIG.USER_TYPES.USER,
    phone: '',
    terms: false
  });
    const userTypeOptions = Object.values(TEXT_CONSTANTS.USER_TYPE_OPTIONS);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }
    
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.terms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      if (onSignup) {
        await onSignup({
          ...formData,
          phone: formData.phone.replace(/\D/g, '') // Clean phone number
        });
      }
      onClose();
    } catch (error) {
      setErrors({ general: error.message || 'Signup failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* General Error */}
      {errors.general && (
        <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg">
          {errors.general}
        </div>
      )}

      {/* User Type Selection */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          I want to register as:
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {userTypeOptions.map((option) => (
            <label
              key={option.value}
              className={combineClasses(
                'relative flex cursor-pointer rounded-lg border p-4 user-type-radio',
                'focus:outline-none transition-all duration-200',
                formData.userType === option.value
                  ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-500 selected'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              )}
            >
              <input
                type="radio"
                name="userType"
                value={option.value}
                checked={formData.userType === option.value}
                onChange={handleInputChange}
                className="sr-only"
              />
              <div className="flex items-center">
                <div className="text-sm">
                  <div className="flex items-center mb-1">
                    <span className="mr-2">{USER_TYPE_ICON[option.value] || '👤'}</span>
                    <span className="font-medium text-gray-900">{option.label.split(' - ')[0]}</span>
                  </div>
                  <p className="text-xs text-gray-600">{option.description}</p>
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Name Field */}
      <div className="space-y-2">
        <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700">
          Full Name
        </label>
        <div className="relative">
          <input
            id="signup-name"
            name="name"
            type="text"
            autoComplete="name"
            value={formData.name}
            onChange={handleInputChange}
            className={combineClasses(
              "w-full px-4 py-3 pl-11 text-sm",
              "border rounded-lg",
              "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
              "transition-colors duration-200",
              errors.name 
                ? "border-red-300 bg-red-50" 
                : "border-gray-300 bg-white hover:border-gray-400"
            )}
            placeholder="Enter your full name"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </div>
        {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
      </div>

      {/* Email Field */}
      <div className="space-y-2">
        <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700">
          Email Address
        </label>
        <div className="relative">
          <input
            id="signup-email"
            name="email"
            type="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleInputChange}
            className={combineClasses(
              "w-full px-4 py-3 pl-11 text-sm",
              "border rounded-lg",
              "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
              "transition-colors duration-200",
              errors.email 
                ? "border-red-300 bg-red-50" 
                : "border-gray-300 bg-white hover:border-gray-400"
            )}
            placeholder="Enter your email"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
            </svg>
          </div>
        </div>
        {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
      </div>

      {/* Phone Field */}
      <div className="space-y-2">
        <label htmlFor="signup-phone" className="block text-sm font-medium text-gray-700">
          Phone Number
        </label>
        <div className="relative">
          <input
            id="signup-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            value={formData.phone}
            onChange={handleInputChange}
            className={combineClasses(
              "w-full px-4 py-3 pl-11 text-sm",
              "border rounded-lg",
              "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
              "transition-colors duration-200",
              errors.phone 
                ? "border-red-300 bg-red-50" 
                : "border-gray-300 bg-white hover:border-gray-400"
            )}
            placeholder="Enter your phone number"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          </div>
        </div>
        {errors.phone && <p className="text-xs text-red-600">{errors.phone}</p>}
      </div>

      {/* Password Fields Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Password Field */}
        <div className="space-y-2">
          <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700">
            Password
          </label>
          <div className="relative">
            <input
              id="signup-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              value={formData.password}
              onChange={handleInputChange}
              className={combineClasses(
                "w-full px-4 py-3 pl-11 pr-11 text-sm",
                "border rounded-lg",
                "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
                "transition-colors duration-200",
                errors.password 
                  ? "border-red-300 bg-red-50" 
                  : "border-gray-300 bg-white hover:border-gray-400"
              )}
              placeholder="Create password"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-600">{errors.password}</p>}
        </div>

        {/* Confirm Password Field */}
        <div className="space-y-2">
          <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="signup-confirm-password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              className={combineClasses(
                "w-full px-4 py-3 pl-11 pr-11 text-sm",
                "border rounded-lg",
                "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500",
                "transition-colors duration-200",
                errors.confirmPassword 
                  ? "border-red-300 bg-red-50" 
                  : "border-gray-300 bg-white hover:border-gray-400"
              )}
              placeholder="Confirm password"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-red-600">{errors.confirmPassword}</p>}
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="space-y-2">
        <label className="flex items-start">
          <input
            type="checkbox"
            name="terms"
            checked={formData.terms}
            onChange={handleInputChange}
            className={combineClasses(
              "w-4 h-4 text-orange-600 bg-white border-gray-300 rounded",
              "focus:ring-orange-500 focus:ring-2",
              "mt-1 flex-shrink-0"
            )}
          />
          <span className="ml-3 text-sm text-gray-600">
            I agree to the{' '}
            <button type="button" className="text-orange-600 hover:text-orange-700 font-medium">
              Terms of Service
            </button>{' '}
            and{' '}
            <button type="button" className="text-orange-600 hover:text-orange-700 font-medium">
              Privacy Policy
            </button>
          </span>
        </label>
        {errors.terms && <p className="text-xs text-red-600">{errors.terms}</p>}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading}
        className={combineClasses(
          "w-full py-3 px-4 text-sm font-medium text-white",
          "bg-gradient-to-r from-orange-500 to-orange-600 auth-button",
          "rounded-lg shadow-lg",
          "focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2",
          "transition-all duration-200",
          isLoading 
            ? "opacity-50 cursor-not-allowed" 
            : "hover:from-orange-600 hover:to-orange-700 hover:shadow-xl transform hover:scale-[1.02]"
        )}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Creating Account...
          </div>
        ) : (
          'Create Account'
        )}
      </button>

      {/* Divider */}
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">or</span>
        </div>
      </div>

      {/* Switch to Login */}
      <div className="text-center">
        <p className="text-sm text-gray-600">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="font-medium text-orange-600 hover:text-orange-700 transition-colors duration-200"
          >
            Sign in here
          </button>
        </p>
      </div>
    </form>
  );
};

export default SignupForm;
